import { useEffect, useState } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

const MONTH_LABELS: Record<string, string> = {
  '01': 'Jan', '02': 'Fév', '03': 'Mar', '04': 'Avr',
  '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Aoû',
  '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Déc',
}

const TYPE_LABELS: Record<string, string> = {
  EVENT: 'Événement',
  TRAINING: 'Formation',
  OTHER: 'Autre',
}

const PIE_COLORS = ['#6E7FFF', '#4ADE80', '#F87171', '#FBBF24']

function fmt(cents: number) {
  return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
}

function fmtLabel(month: string) {
  const [year, m] = month.split('-')
  return `${MONTH_LABELS[m]} ${year.slice(2)}`
}

interface Stats {
  kpis: {
    avgDelayH: number | null
    totalRevenue: number
    totalCommission: number
    assignmentRate: number
    totalMissions: number
    completed: number
  }
  byMonth: { month: string; revenue: number }[]
  byType: { type: string; revenue: number }[]
  topCompanies: { name: string; revenue: number; missionCount: number }[]
  topProviders: { name: string; rating: number; ratingCount: number; assignmentCount: number }[]
  missionsByStatus: Record<string, number>
}

function KpiCard({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon: string }) {
  return (
    <div className="glass-card p-6 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="text-3xl font-bold text-text-primary">{value}</p>
      <p className="text-text-secondary text-sm">{label}</p>
      {sub && <p className="text-text-muted text-xs">{sub}</p>}
    </div>
  )
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null
  return (
    <div className="glass-card p-3 text-sm">
      <p className="text-text-secondary mb-1">{fmtLabel(label)}</p>
      <p className="text-accent font-semibold">{fmt(payload[0].value)}</p>
    </div>
  )
}

export default function StatsPage() {
  const [stats, setStats] = useState<Stats | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const token = localStorage.getItem('velink_token')
    fetch(`${API}/api/admin/stats`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('Non autorisé')
        return r.json()
      })
      .then(setStats)
      .catch((e) => setError(e.message))
  }, [])

  if (error) return (
    <div className="flex items-center justify-center h-64">
      <div className="glass-card p-6 text-danger text-center">
        <p className="font-semibold mb-2">Accès refusé</p>
        <p className="text-sm text-text-secondary">{error}</p>
      </div>
    </div>
  )

  if (!stats) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-text-muted animate-pulse">Chargement…</div>
    </div>
  )

  const { kpis, byMonth, byType, topCompanies, topProviders } = stats

  const delayLabel = kpis.avgDelayH === null
    ? 'N/A'
    : kpis.avgDelayH < 1
      ? `${Math.round(kpis.avgDelayH * 60)} min`
      : `${kpis.avgDelayH} h`

  const pieData = byType.map((b) => ({
    name: TYPE_LABELS[b.type] ?? b.type,
    value: b.revenue,
  }))

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Statistiques</h1>
        <p className="text-text-secondary text-sm mt-1">Indicateurs clés de la plateforme</p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon="⏱️"
          label="Délai moyen d'assignation"
          value={delayLabel}
          sub="Temps entre création et acceptation"
        />
        <KpiCard
          icon="💶"
          label="CA total généré"
          value={fmt(kpis.totalRevenue)}
          sub={`Commission Velink : ${fmt(kpis.totalCommission)}`}
        />
        <KpiCard
          icon="✅"
          label="Taux d'assignation"
          value={`${kpis.assignmentRate} %`}
          sub="Missions assignées / missions actives"
        />
        <KpiCard
          icon="🏁"
          label="Missions complétées"
          value={String(kpis.completed)}
          sub={`Sur ${kpis.totalMissions} missions totales`}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly revenue bar chart */}
        <div className="lg:col-span-2 glass-card p-6">
          <h2 className="text-text-primary font-semibold mb-4">CA mensuel (6 derniers mois)</h2>
          {byMonth.every((m) => m.revenue === 0) ? (
            <div className="flex items-center justify-center h-48 text-text-muted text-sm">
              Aucun paiement capturé sur la période
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={byMonth} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="month"
                  tickFormatter={fmtLabel}
                  tick={{ fill: '#A8A8B8', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => `${(v / 100).toLocaleString('fr-FR')} €`}
                  tick={{ fill: '#A8A8B8', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={80}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(110,127,255,0.08)' }} />
                <Bar dataKey="revenue" fill="#6E7FFF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Revenue by type pie chart */}
        <div className="glass-card p-6">
          <h2 className="text-text-primary font-semibold mb-4">CA par type</h2>
          {pieData.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-text-muted text-sm">
              Aucune donnée
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="45%"
                  outerRadius={75}
                  label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`}
                  labelLine={false}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v: number) => fmt(v)}
                  contentStyle={{ background: 'rgba(10,10,20,0.9)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                  labelStyle={{ color: '#A8A8B8' }}
                  itemStyle={{ color: '#F0F0F5' }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Tables row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top companies */}
        <div className="glass-card p-6">
          <h2 className="text-text-primary font-semibold mb-4">Top entreprises par CA</h2>
          {topCompanies.length === 0 ? (
            <p className="text-text-muted text-sm">Aucune donnée</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-muted text-xs">
                  <th className="text-left pb-2 font-medium">Entreprise</th>
                  <th className="text-right pb-2 font-medium">Missions</th>
                  <th className="text-right pb-2 font-medium">CA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {topCompanies.map((c, i) => (
                  <tr key={i} className="text-text-secondary hover:text-text-primary transition-colors">
                    <td className="py-2.5 flex items-center gap-2">
                      <span className="text-xs text-text-muted w-5">{i + 1}.</span>
                      <span className="truncate max-w-[160px]">{c.name}</span>
                    </td>
                    <td className="py-2.5 text-right">{c.missionCount}</td>
                    <td className="py-2.5 text-right text-accent font-medium">{fmt(c.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Top providers */}
        <div className="glass-card p-6">
          <h2 className="text-text-primary font-semibold mb-4">Top prestataires par missions</h2>
          {topProviders.length === 0 ? (
            <p className="text-text-muted text-sm">Aucune donnée</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-text-muted text-xs">
                  <th className="text-left pb-2 font-medium">Prestataire</th>
                  <th className="text-right pb-2 font-medium">Note</th>
                  <th className="text-right pb-2 font-medium">Missions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {topProviders.map((p, i) => (
                  <tr key={i} className="text-text-secondary hover:text-text-primary transition-colors">
                    <td className="py-2.5 flex items-center gap-2">
                      <span className="text-xs text-text-muted w-5">{i + 1}.</span>
                      <span className="truncate max-w-[160px]">{p.name}</span>
                    </td>
                    <td className="py-2.5 text-right">
                      {p.ratingCount > 0 ? (
                        <span className="text-[#FBBF24]">★ {p.rating.toFixed(1)}</span>
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="py-2.5 text-right font-medium">{p.assignmentCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
